import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight, Loader2, Rocket } from 'lucide-react';
import Logo from '../../components/home/Logo';
import { stripeService } from '../../services/StripeService';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [stage, setStage] = useState('success'); // 'success' | 'transition'
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Rede de segurança: força sync da assinatura com o Stripe logo após o pagamento,
    // para o caso do webhook não ter processado o evento a tempo.
    // Silencioso — não bloqueia a UI nem propaga erro.
    stripeService.syncMySubscription();

    // Dispara evento de conversão para GTM → Pixel (Facebook/Meta, Google Ads, etc.)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: sessionId,
        currency: 'BRL',
      }
    });

    // Countdown para mostrar transição
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Mudar para estágio de transição
          setStage('transition');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Quando muda para transição, esperar 2s e ir para onboarding
  useEffect(() => {
    if (stage === 'transition') {
      const timeout = setTimeout(() => {
        navigate('/onboarding');
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [stage, navigate]);

  // Ir direto para onboarding
  const handleStartOnboarding = () => {
    navigate('/onboarding');
  };

  // Pular onboarding
  const handleSkipOnboarding = () => {
    navigate('/contents');
  };

  // Tela de transição para onboarding
  if (stage === 'transition') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-lg w-full text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img src="/logo.png" alt="hello, social" className="h-16 object-contain" />
          </div>

          {/* Rocket Animation */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full shadow-lg animate-pulse">
              <Rocket className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Transition Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Vamos personalizar sua experiência! ✨
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Em alguns segundos vamos iniciar uma configuração rápida para 
            criar conteúdos ainda mais alinhados com seu negócio.
          </p>

          {/* Loading */}
          <div className="flex items-center justify-center gap-3 text-amber-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-medium">Preparando onboarding...</span>
          </div>
        </div>
      </div>
    );
  }

  // Tela de sucesso inicial
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-lg w-full text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src="/logo.png" alt="hello, social" className="h-12 object-contain" />
        </div>

        {/* Success Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-lg mb-6 animate-bounce-slow">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Parabéns! 🎉
        </h1>
        <p className="text-xl text-gray-600 mb-2">
          Sua assinatura foi ativada com sucesso!
        </p>
        <p className="text-gray-500 mb-8">
          Agora vamos personalizar sua experiência na plataforma
        </p>

        {/* Features unlocked */}
        <div className="bg-white rounded-2xl border-2 border-green-200 p-6 mb-8 text-left">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            O que você desbloqueou:
          </h3>
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Geração de imagens por IA</span>
            </li>
            <li className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Templates profissionais</span>
            </li>
            <li className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Legendas automáticas</span>
            </li>
            <li className="flex items-center gap-3 text-gray-700">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Publicação automática</span>
            </li>
          </ul>
        </div>

        {/* Créditos liberados */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
          <p className="text-green-800 text-sm">
            <strong>Pronto!</strong> Seus créditos do mês já estão liberados —
            cada imagem custa 1 crédito e cada vídeo custa 5.
          </p>
        </div>

        {/* Next Step Info */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h4 className="font-bold text-gray-900">Próximo passo</h4>
              <p className="text-sm text-gray-600">Configuração inicial (~2 min)</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 text-left">
            Vamos fazer algumas perguntas rápidas para personalizar a geração 
            de conteúdo de acordo com seu negócio e estilo.
          </p>
        </div>

        {/* CTA Buttons */}
        <button
          onClick={handleStartOnboarding}
          className="w-full py-4 px-6 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-bold rounded-xl transition-all transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2 mb-3"
        >
          Começar configuração
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={handleSkipOnboarding}
          className="w-full py-3 px-6 text-gray-600 font-medium hover:text-gray-800 transition-colors"
        >
          Pular e ir direto para a plataforma
        </button>

        {/* Countdown */}
        <p className="text-sm text-gray-400 mt-6 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Iniciando automaticamente em {countdown}s...
        </p>
      </div>

      {/* Confetti effect (CSS animation) */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CheckoutSuccess;